import { vehicleModelApi } from "#/api/vehicleModelApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import type { VehicleModel } from "#/types/vehicle-model";
import { Button, Modal, Skeleton, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconEye,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import { useTableColumns } from "#/hooks/useTableColumns";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const VehicleModelsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [vehicleModelToDelete, setVehicleModelToDelete] =
    useState<VehicleModel>();

  const [sortBy, setSortBy] = useState<string>("updatedAt");

  // Columns come from the shared config filtered by the client's Column
  // Visibility setting; the Actions column is appended by the page.
  const { visible, headers: columnHeaders, sortableColumns, sortKeyAt, indexOf } =
    useTableColumns<"vehicleModels", VehicleModel>("vehicleModels", {
      id: {
        cellClassName: "min-w-[100px]",
        cell: (item) => item.id ?? "--",
      },
      name: {
        sortKey: "name",
        cellClassName: "min-w-[180px]",
        cell: (item) => item.name ?? "--",
      },
      brand: {
        sortKey: "brand",
        cellClassName: "capitalize min-w-[150px]",
        cell: (item) => item.brand ?? "--",
      },
      model: {
        sortKey: "model",
        cellClassName: "capitalize min-w-[150px]",
        cell: (item) => item.model ?? "--",
      },
      size: {
        sortKey: "size",
        cellClassName: "capitalize min-w-[120px]",
        cell: (item) => item.size ?? "--",
      },
      updatedAt: {
        sortKey: "updatedAt",
        cellClassName: "min-w-[180px]",
        cell: (item) =>
          item.updatedAt ? (
            <span>
              {formatDate(item.updatedAt, client)}
              <br />
              {formatTime(item.updatedAt, client)}
            </span>
          ) : (
            "--"
          ),
      },
    });
  const defaultSortIndex = indexOf("updatedAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getVehicleModels,
  } = useQuery({
    queryKey: ["vehicle-models", sortBy, page, sortBy, sortDirection],
    queryFn: async () => {
      try {
        const response = await vehicleModelApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No vehicle models returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  // What depends on the model about to be deleted. Deletion is refused by the
  // backend while vehicles use it, so the dialog says so instead of offering it.
  const deleteTargetId = vehicleModelToDelete?.id;
  const { data: deletePreview, isPending: deletePreviewPending } = useQuery({
    queryKey: ["vehicleModelDeletePreview", deleteTargetId],
    queryFn: async () => {
      if (deleteTargetId === undefined) throw new Error("No vehicle model selected");
      const response = await vehicleModelApi.getDeletePreview(deleteTargetId);
      return response.data;
    },
    enabled: deleteModalOpened && deleteTargetId !== undefined,
    staleTime: 0,
  });
  const deleteBlocked = deletePreview !== undefined && !deletePreview.can_delete;

  const deleteVehicleModelMutation = useMutation({
    mutationFn: async (id: number) => {
      return await vehicleModelApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("vehicleModels.deleteModel.success"),
      });
      closeDeleteModal();
      setVehicleModelToDelete(undefined);
      void getVehicleModels();
    },
    onError: (error) => {
      logger.error("deleteVehicleModel error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("vehicleModels.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                void navigate("/fleet/models/new");
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("vehicleModels.createNewModel")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...columnHeaders, t("common.actions")]}
          loading={isPending}
          allowSorting={true}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          defaultSort={
            defaultSortIndex !== undefined
              ? { direction: "DESC", index: defaultSortIndex }
              : undefined
          }
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/models/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setVehicleModelToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        size="lg"
        title={t("vehicleModels.deleteModel.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col gap-5">
          <div className="flex flex-col gap-3 text-sm">
            <span>{t("vehicleModels.deleteModel.description")}</span>
            <div className="flex flex-col gap-1 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{vehicleModelToDelete?.name ?? "--"}</span>
              <span className="text-xs text-gray-600">
                {vehicleModelToDelete?.brand} - {vehicleModelToDelete?.model} ({vehicleModelToDelete?.size})
              </span>
            </div>
            {deletePreviewPending ? (
              <Skeleton height={96} radius="sm" />
            ) : deleteBlocked ? (
              <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
                <div className="flex items-start gap-2">
                  <IconAlertTriangle size={18} className="mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">{t("vehicleModels.deleteModel.blocked")}</span>
                    <span>
                      {t("vehicleModels.deleteModel.inUse", {
                        vehicles: deletePreview.vehicle_count,
                        shipments: deletePreview.shipment_count,
                      })}
                    </span>
                    <span>{t("vehicleModels.deleteModel.reassignFirst")}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 rounded-md border border-red-200 bg-white p-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {t("vehicleModels.deleteModel.vehiclesUsing")}
                  </span>
                  <ul className="flex max-h-56 flex-col divide-y divide-gray-100 overflow-y-auto">
                    {deletePreview.vehicles.map((vehicle) => (
                      <li
                        key={vehicle.id}
                        className="flex items-center justify-between gap-4 py-2 text-gray-900"
                      >
                        <a
                          href={`/fleet/vehicles/${vehicle.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t("vehicleModels.deleteModel.openVehicle")}
                          className="flex min-w-0 items-center gap-1 font-medium text-blue-600 underline-offset-2 hover:underline"
                        >
                          <span className="truncate">{vehicle.name.trim() || `#${vehicle.id}`}</span>
                          <IconArrowUpRight size={14} className="shrink-0" />
                        </a>
                        <span className="shrink-0 text-gray-600">
                          {t("vehicleModels.deleteModel.shipments", { count: vehicle.shipments })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <span>{t("vehicleModels.deleteModel.warning")}</span>
                <span className="font-bold">{t("vehicleModels.deleteModel.confirmation")}</span>
              </div>
            )}
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (vehicleModelToDelete) {
                    deleteVehicleModelMutation.mutate(vehicleModelToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteVehicleModelMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete() || deletePreviewPending || deleteBlocked}
              >
                {t("vehicleModels.deleteModel.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleModelsPage;
